/**
 * resolveListingVariant — pure, deterministic mapping from
 * `(location, configuration, user)` to a complete `ListingVariant`.
 *
 * No React, no hooks, no I/O. All i18n references are KEY strings. The
 * resolver picks a base variant by `configuration.dealType` (with
 * `derivedBrokerMode` as the price-threshold fallback for the 99% of
 * listings that ship without an explicit `dealType`), layers on runtime
 * overrides (admin panel, quantity input), and returns a frozen-shape
 * object that downstream components read from.
 *
 * Fleet note: the fleet sub-variant is NOT auto-resolved here. Monthly
 * + Parking + multi-unit listings continue to resolve to the Monthly
 * variant; the PriceCard renders an additional fleet pill that opens
 * the LeadForm with `variant="fleet"` (Unit 8 consumes
 * `baseFleetVariant` directly). Admin querystring overrides
 * (`?variant=...`) are handled in the Provider (Unit 2), not here.
 */

import type { IConfiguration, ILocation, IUser } from 'src/types/graphql'
import { DealType, LocationType } from 'src/types/graphql'

import type {
  AdminPanelConfig,
  BaseListingVariant,
  ListingVariant,
  VariantKey
} from 'src/lib/listingVariants/types'
import { baseBrokerVariant } from 'src/lib/listingVariants/variants/broker'
import { baseMonthlyVariant } from 'src/lib/listingVariants/variants/monthly'
import { baseTraditionalVariant } from 'src/lib/listingVariants/variants/traditional'
import { getVertical } from 'src/utils/pixels/enrichment'
import { getEffectiveDealType, isB2BPillarListing } from 'src/utils/pricing'

/**
 * Availability threshold above which a parking listing surfaces the
 * fleet-trigger pill. "Flotillas" = parking with >5 units available,
 * where the listing reads as fleet-grade (a B2B opportunity for a
 * multi-vehicle company), not a single-spot rental. Applies to Monthly
 * AND Traditional parking (Traditional added 2026-08-20 with the first
 * Traditional parking operator — Entra); Broker keeps its own funnel.
 * The resolver folds this into `priceCard.showFleetCta`; PriceCard +
 * Header read that resolved flag. Source of truth — do not inline the
 * literal or re-derive the gate in components.
 */
export const FLEET_TRIGGER_AVAILABILITY_THRESHOLD = 5

export type IUserLike = Pick<IUser, 'isAdmin' | 'id'> | null

/**
 * Variant-discriminator map. We never collapse the values inline —
 * mirroring the `PILLAR_PAGE_MAP` / `CUSTOMER_TYPE_MAP` shape used
 * elsewhere in the codebase keeps the variant table grep-able.
 */
const BASE_VARIANT_BY_DEAL_TYPE: { [key in DealType]: BaseListingVariant } = {
  [DealType.Monthly]: baseMonthlyVariant,
  [DealType.Broker]: baseBrokerVariant,
  [DealType.Traditional]: baseTraditionalVariant
}

const VARIANT_KEY_BY_DEAL_TYPE: { [key in DealType]: VariantKey } = {
  [DealType.Monthly]: 'monthly',
  [DealType.Broker]: 'broker',
  [DealType.Traditional]: 'traditional'
}

const ADMIN_PANEL_CONFIG: AdminPanelConfig = {
  showFunnelWidget: true,
  showVariantOverride: true,
  showPreviewAs: true,
  showEditListingLink: true
}

function isKnownDealType(value: unknown): value is DealType {
  return (
    value === DealType.Monthly ||
    value === DealType.Broker ||
    value === DealType.Traditional
  )
}

// `isB2BPillarListing` is now centralized in `src/utils/pricing.ts` so both
// this resolver AND analytics enrichment (`buildEnrichment`) apply the same
// warehouse/industrial promotion rule. Drift between the two re-opens the
// sub-$30k-bodega leak where the listing page rendered Broker but events
// fired with deal_type=monthly and polluted the storage tier rollup.

export function resolveListingVariant(
  location: ILocation,
  configuration: IConfiguration,
  user: IUserLike,
  /**
   * Optional escape hatch: price-derived broker classification. 99% of
   * listings ship with `configuration.dealType` UNSET; the legacy code
   * decided broker-vs-monthly by `price > PRICE_THRESHOLD` via
   * `useGetPrice().getIsBrokerMode(config)`. That helper is a React hook
   * (it reads the currency provider), so the resolver itself can't call
   * it. Instead the `ListingVariantProvider` computes the boolean and
   * threads it in here.
   *
   * Discrimination order:
   *   1. `dealType === Traditional` → traditional (explicit always wins)
   *   2. `derivedBrokerMode === true` (price > $30k) → broker
   *   3. B2B pillar (`pillarPage` is `renta-bodegas-comerciales` or
   *      `renta-naves-industriales`) → broker
   *   4. otherwise → monthly
   *
   * **Reading B:** explicit `dealType` (including `Monthly`) wins over
   * the price + pillar promotions. A host who tags a bodega comercial
   * as Monthly gets the Monthly listing page (on-platform Reservar) and
   * the Monthly funnel events. Rare but allowed.
   *
   * Pillar promotion mirrors backend `resolveRenterCustomerType` + the
   * LangGraph analysis prompt for the un-tagged majority of listings
   * (~99% have `dealType` unset).
   *
   * The optional default keeps existing callers (tests, surfaces that
   * don't pass the 4th arg) backwards-compatible — they get
   * dealType + pillar-based behavior, collapsing to Monthly when the
   * field is missing.
   */
  derivedBrokerMode: boolean = false
): ListingVariant {
  // 1. Discriminate. Explicit dealType ALWAYS wins (Reading B). Only when
  //    dealType is unset do we fall back to price + pillar inference.
  let dealType: DealType
  if (isKnownDealType(configuration.dealType)) {
    dealType = configuration.dealType
  } else if (derivedBrokerMode === true || isB2BPillarListing(configuration)) {
    dealType = DealType.Broker
  } else {
    dealType = DealType.Monthly
  }
  const base = BASE_VARIANT_BY_DEAL_TYPE[dealType]
  const variantKey = VARIANT_KEY_BY_DEAL_TYPE[dealType]

  // 2. Runtime override: quantity input. Only Monthly + multi-unit
  //    listings get the +/- selector. Broker/Traditional/Fleet always
  //    surface a single CTA and capture quantity via the lead form.
  const availability = configuration.availability ?? 0
  const showQuantityInput = variantKey === 'monthly' && availability > 1

  // Fleet pill verdict: base policy (monthly + traditional) AND parking
  // vertical AND fleet-grade availability. Same base-then-overwrite
  // pattern as `showQuantityInput` — components read the resolved flag,
  // never re-derive the gate.
  const showFleetCta =
    base.priceCard.showFleetCta &&
    getVertical(configuration.locationType) === 'parking' &&
    availability > FLEET_TRIGGER_AVAILABILITY_THRESHOLD

  const priceCard = {
    ...base.priceCard,
    showQuantityInput,
    showFleetCta
  }

  // 3. Compute meta. Note `locationType` lives on the Configuration in
  //    this schema, not the Location — the `location` param is still
  //    part of the signature for forward compatibility (e.g. when
  //    sections need address-derived behavior).
  void location
  const locationType: LocationType =
    configuration.locationType ?? LocationType.Other
  const isAdmin = Boolean(user?.isAdmin)
  const isAuthed = user !== null

  // 4. Admin panel: present iff user is admin.
  const admin: AdminPanelConfig | null = isAdmin ? ADMIN_PANEL_CONFIG : null

  return {
    meta: {
      dealType,
      locationType,
      isAdmin,
      isAuthed,
      variantKey
    },
    priceCard,
    leadForm: base.leadForm,
    sections: base.sections,
    checkout: base.checkout,
    admin
  }
}

/**
 * Does this listing disclose its landlord to renters?
 *
 * `true` only when EVERY configuration on the listing resolves to a variant
 * whose host section is `'host'` — the NAMED masthead.
 *
 * 🛑 As of 2026-09-09 that is **no variant**, so this returns `false` for
 * every listing: Monthly moved to `'anonymous'`, Broker shows the specialist
 * card and Traditional hides the section. (Fleet is not in that list because
 * **Fleet is never a resolved listing variant at all** — see the Fleet note in
 * this file's header: a fleet-grade parking listing resolves to Monthly or
 * Traditional and only the LeadForm consumes `baseFleetVariant`.) Exposing the operator
 * pre-checkout is the disintermediation leak the variant table exists to
 * prevent, and Monthly is where a bypass costs the most (a recurring
 * commission, not a one-off lead fee). The function is kept — rather than
 * inlined to `false` — because it is the ONE place the props scrub, the meta
 * tags and the review byline agree on the answer; if a variant ever opts back
 * into `'host'`, all three follow automatically. The renter can also switch
 * configuration client-side, so one withholding configuration is enough to
 * withhold the identity for the whole page.
 *
 * Reads the SAME variant table the page renders from, so the two can't drift.
 * The price-threshold promotion is supplied by `getEffectiveDealType` — the
 * canonical non-hook resolver — because the currency-aware `useGetPrice`
 * hook this function's callers would otherwise need isn't available outside
 * React (and `getStaticProps` is the main caller).
 *
 * Callers: the listing page's `getStaticProps`, which runs the location
 * through `scrubHostIdentity` when this is false and passes the same verdict
 * down as `disclosesHost` so `product:brand` / the JSON-LD `manufacturerName`
 * fall back to SpotMe rather than to the individual submitter's personal name.
 * `Details/index.tsx` reads the variant directly for the review byline.
 */
/**
 * Remove every landlord-identifying field from a location before it ships to
 * the browser.
 *
 * 🛑 HIDING THE SECTION IS NOT ENOUGH. `getStaticProps` serialises whatever it
 * returns into `__NEXT_DATA__` on a publicly-served HTML page, so an unrendered
 * name is still one "view source" away. Three carriers, and dropping only the
 * first is the trap: `location.team` (the operator brand) was closed first, but
 * `location.user` still carried the individual host's first/last name, photo
 * and blurb, and BOTH the user-nested and top-level `bussinessProfile` carried
 * a trading name. `getLocationProfile` falls through team → businessProfile →
 * user, so scrubbing only the team makes the page name a PERSON instead — a
 * worse disclosure than the one being fixed.
 *
 * `id` and `isVerified` survive on purpose: the anonymous host block renders
 * the verification rows from `isVerified`, and review pagination resolves off
 * the owner `id` (an opaque UUID, already public in the review query).
 */
export function scrubHostIdentity(location: ILocation): ILocation {
  const { user } = location
  return {
    ...location,
    team: null,
    bussinessProfile: null,
    user: user
      ? ({
          ...user,
          firstname: null,
          lastname: null,
          description: null,
          profilePicture: null,
          bussinessProfile: null,
          userChanges: null
        } as ILocation['user'])
      : user
  }
}

export function listingDisclosesHost(location: ILocation): boolean {
  const configurations = location.configurations?.length
    ? location.configurations
    : location.defaultConfiguration
      ? [location.defaultConfiguration]
      : []
  if (configurations.length === 0) return false
  return configurations.every((configuration) => {
    const derivedBrokerMode =
      getEffectiveDealType(configuration) === DealType.Broker
    return (
      resolveListingVariant(location, configuration, null, derivedBrokerMode)
        .sections.user.variant === 'host'
    )
  })
}
